import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import PropTypes from 'prop-types';
import { isBlank } from 'utils/helperMethods';
import Loader from 'sharedComponents/Loader';
import useScreen from 'hooks/useScreen';

import SubscriptionInfoPresenter from 'presenters/SubscriptionInfoPresenter';

import UserPresenter from 'presenters/UserPresenter';
import useCreditCard from 'reducers/hooks/loanOfficer/useCreditCard';
import useSubscriptionInfo from 'reducers/hooks/loanOfficer/useSubscriptionInfo';
import useSubscription from 'reducers/hooks/loanOfficer/useSubscription';
import useApp from 'reducers/hooks/useApp';

import CreaditCardModal from './components/CreditCardModal';
import SubscriptionInfo from './components/SubscriptionInfo';
import UpcomingInvoice from './components/UpcomingInvoice';
import Connections from './components/Connections';
import Budget from './components/Budget';
import PaymentMethod from './components/PaymentMethod';

import Title from '../Title';

import useStyles from './useStyles';

const SubscriptionPage = props => {
  const classes = useStyles();
  const { t } = useTranslation();
  const { isMobile } = useScreen();
  const { onBack } = props;

  const { createSubscription, isSubscriptionCreating } = useSubscription();
  const { createCreditCard, isCreditCardCreating } = useCreditCard();
  const { updateLoanOfficer, isUpdating, currentUser } = useApp();

  const [isCreditCardModalOpen, setIsCreditCardModalOpen] = useState(false);
  const [shouldUpdateSubscription, setShouldUpdateSubscription] = useState(false);
  const [subscriptionCreateParams, setSubscriptionCreateParams] = useState(null);

  const dailyMaxConnectionsCount = UserPresenter.dailyMaxConnectionsCount(currentUser);
  const weeklyMaxConnectionsCount = UserPresenter.weeklyMaxConnectionsCount(currentUser);

  const {
    loadSubscriptionInfo,
    subscriptionInfo,
    isSubscriptionInfoLoading,
    updateSubscriptionInfo,
    isSubscriptionInfoUpdating,
  } = useSubscriptionInfo();

  useEffect(() => {
    loadSubscriptionInfo();
  }, []);

  if (isSubscriptionInfoLoading || isBlank(subscriptionInfo)) {
    return (
      <div className={classes.loader}>
        <Loader />
      </div>
    );
  }

  const hasPaidSubscriptions = SubscriptionInfoPresenter.hasPaidSubscriptions(subscriptionInfo);
  const hasAnyLicensedStates = SubscriptionInfoPresenter.hasAnyLicensedStates(subscriptionInfo);
  const hasCreditCard = SubscriptionInfoPresenter.hasCreditCard(subscriptionInfo);
  const hasUpcomingInvoice = SubscriptionInfoPresenter.hasUpcomingInvoice(subscriptionInfo);
  const disableAllConnections = UserPresenter.disableAllConnections(currentUser);
  const hasPaidSubscriptionsAndLicensedStates = hasPaidSubscriptions && hasAnyLicensedStates;

  const handleSaveSubscription = async params => {
    setShouldUpdateSubscription(false);
    await createSubscription(params);
    loadSubscriptionInfo();
  };

  const handleChangeSubscription = params => {
    if (!hasCreditCard) {
      setShouldUpdateSubscription(true);
      setSubscriptionCreateParams(params);
      setIsCreditCardModalOpen(true);
      return;
    }
    handleSaveSubscription(params);
  };

  const handleOpenCreditCardModal = () => {
    setIsCreditCardModalOpen(true);
  };

  const handleFinishCreditCardCreating = () => {
    setIsCreditCardModalOpen(false);
    if (!shouldUpdateSubscription) {
      loadSubscriptionInfo();
      return;
    }
    handleSaveSubscription(subscriptionCreateParams);
  };

  const handleCloseCreaditCardModal = () => {
    setIsCreditCardModalOpen(false);
  };

  return (
    <div className={classes.container}>
      {isMobile && <Title text={t('components.SubscriptionPage.title')} onBack={onBack} />}
      {!hasPaidSubscriptions && (
        <div className={classes.subscriptionInfo}>
          <SubscriptionInfo
            subscriptionInfo={subscriptionInfo}
            isLoading={isSubscriptionCreating}
            hasLicensedStates={hasAnyLicensedStates}
            onFinish={handleChangeSubscription}
          />
        </div>
      )}
      {hasPaidSubscriptionsAndLicensedStates && (
        <div className={classes.connections}>
          <Connections
            isAllConnectionsDisabled={disableAllConnections}
            onConnectionsChange={updateLoanOfficer}
            isConnectionsPending={isUpdating}
            subscriptionInfo={subscriptionInfo}
            onUpdate={updateSubscriptionInfo}
            isSubscriptionInfoUpdating={isSubscriptionInfoUpdating}
          />
        </div>
      )}
      {hasUpcomingInvoice && (
        <div className={classes.upcomingInvoice}>
          <UpcomingInvoice subscriptionInfo={subscriptionInfo} />
        </div>
      )}
      {hasPaidSubscriptions && (
        <div className={classes.budget}>
          <Budget
            weeklyMaxConnectionsCount={weeklyMaxConnectionsCount}
            dailyMaxConnectionsCount={dailyMaxConnectionsCount}
            onChange={updateLoanOfficer}
            isLoading={isUpdating}
          />
        </div>
      )}
      {hasCreditCard && (
        <div className={classes.paymentMethod}>
          <PaymentMethod onCreditCardModalOpen={handleOpenCreditCardModal} subscriptionInfo={subscriptionInfo} />
        </div>
      )}
      {isCreditCardModalOpen && (
        <CreaditCardModal
          onSave={createCreditCard}
          onClose={handleCloseCreaditCardModal}
          onFinish={handleFinishCreditCardCreating}
          isLoading={isCreditCardCreating}
        />
      )}
    </div>
  );
};

SubscriptionPage.propTypes = {
  onBack: PropTypes.func.isRequired,
};

export default SubscriptionPage;



// WEBPACK FOOTER //
// ./src/components/LoanOfficerSettings/components/SubscriptionPage/SubscriptionPage.js